import type { FC } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Divider, Fade, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import NotesIcon from '@mui/icons-material/Notes'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'

import { AgentModeBadge } from '@/features/events/conversation/agent-mode-badges/AgentModeBadge'
import { ZoomControls } from '@/features/events/conversation/controls/components/ZoomControls'
import { PlanApprovalContextRenderer } from '@/features/events/conversation/renderers/PlanApprovalContextRenderer'
import { UserOptionsRenderer } from '@/features/events/conversation/dialogs'
import { AgentMode } from '@/features/events/shared/types'
import type { UserOption } from '@/features/events/shared/types'

import { HOME_CONTENT } from '../defaults'
import {
  cardSectionContainerSx,
  cardSectionTitleSx,
  dialogBadgeWrapperSx,
  dialogBreadcrumbSx,
  dialogCloseIconButtonSx,
  dialogControlsRowCenterSx,
  dialogControlsRowLeftSx,
  dialogControlsRowRightSx,
  dialogControlsRowSx,
  dialogPassiveIconButtonSx,
  dialogPointerEventsNoneSx,
  dialogTitleBarSx,
  dialogTitleContentSx,
  dialogTitleRowSx,
  dialogTitleTextSx,
  dialogWindowControlsSx,
  visuallyHiddenSx,
} from '../styles'
import { MobileScreenshotFallback } from './MobileScreenshotFallback'

const SECTION_HEADING_ID = 'event-detail-demo-heading'
const PLAN_SELECTION_COLOR = '#00acc1'

const DEMO_PLAN_CONTENT = `## Implementation Plan

### 1. Create \`src/auth/pkce.ts\`

| Component | Purpose | Standard |
|-----------|---------|----------|
| Code Verifier | 43\u2013128 char cryptographic random | RFC 7636 \u00A74.1 |
| Code Challenge | \`Base64url(SHA-256(verifier))\` | RFC 7636 \u00A74.2 |
| State Parameter | CSRF protection nonce | RFC 6749 \u00A710.12 |

\`\`\`typescript
export async function generateChallenge(): Promise<PKCEPair> {
  const verifier = crypto.randomUUID() + crypto.randomUUID()
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  )
  return {
    verifier,
    challenge: base64url(digest),
    method: 'S256' as const,
  }
}
\`\`\`

### 2. Update \`src/auth/client.ts\`
- Store verifier in **\`sessionStorage\`** before redirect
- Attach \`code_challenge\` + \`code_challenge_method\` to \`/authorize\`
- Exchange \`code\` + \`code_verifier\` at token endpoint

### 3. Token refresh rotation
> **Security:** Each refresh rotates the token \u2014 replay attacks are detected immediately.

- Intercept \`401\` with Axios request interceptor
- Queue concurrent requests during refresh cycle
- Retry queued requests with fresh access token`

const DEMO_PLAN_OPTIONS: readonly UserOption[] = [
  { index: 1, option: 'Yes, clear context and auto-accept edits' },
  { index: 2, option: 'Yes, auto-accept edits' },
  { index: 3, option: 'Yes, manually approve edits' },
  { index: 4, option: 'Type here to tell Claude what to change' },
]

const DEMO_SELECTED_OPTION_INDEX = 2

const NOOP = () => {}

export const EventDetailDemoSection: FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  if (isMobile) {
    return (
      <MobileScreenshotFallback
        headingId={SECTION_HEADING_ID}
        title={HOME_CONTENT.eventDetail.title}
        subtitle={HOME_CONTENT.eventDetail.description}
        imageSrc={HOME_CONTENT.eventDetail.image}
        imageAlt={HOME_CONTENT.eventDetail.imageAlt}
      />
    )
  }

  return (
    <Box
      component="section"
      role="region"
      aria-labelledby={SECTION_HEADING_ID}
      sx={cardSectionContainerSx}
    >
      <Typography id={SECTION_HEADING_ID} component="h2" sx={cardSectionTitleSx}>
        {HOME_CONTENT.eventDetail.title}
      </Typography>
      <Typography sx={sectionSubtitleSx}>{HOME_CONTENT.eventDetail.description}</Typography>

      <Fade in={true} timeout={600}>
        <Box
          role="img"
          aria-label="Demonstration of the event detail view showing a plan approval request with rich markdown content and user approval options"
          sx={chromeContainerSx}
        >
          <Typography sx={visuallyHiddenSx}>
            A plan approval event detail view displaying an OAuth2 PKCE implementation plan with
            headings, a standards reference table, a TypeScript code block, bullet points, a
            blockquote, and four approval options with the second option pre-selected.
          </Typography>

          <Box sx={dialogTitleBarSx}>
            <Box sx={dialogTitleRowSx}>
              <Box sx={dialogTitleContentSx}>
                <Box sx={dialogBadgeWrapperSx}>
                  <AgentModeBadge agentMode={AgentMode.PLAN_MODE} />
                </Box>

                <Typography variant="h6" sx={dialogTitleTextSx}>
                  Plan approval request
                </Typography>

                <Typography variant="body2" sx={dialogBreadcrumbSx}>
                  MacBook Pro &gt; Nexus Web &gt; Implement OAuth2
                </Typography>
              </Box>

              <Box sx={dialogWindowControlsSx}>
                <IconButton
                  size="small"
                  sx={dialogPassiveIconButtonSx}
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  <NotesIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={dialogPassiveIconButtonSx}
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={dialogCloseIconButtonSx(theme)}
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box sx={dialogControlsRowSx}>
              <Box sx={dialogControlsRowLeftSx}>
                <Box sx={dialogPointerEventsNoneSx}>
                  <ZoomControls zoomLevel={1} onZoomIn={NOOP} onZoomOut={NOOP} onZoomReset={NOOP} />
                </Box>
              </Box>
              <Box sx={dialogControlsRowCenterSx} />
              <Box sx={dialogControlsRowRightSx} />
            </Box>
          </Box>

          <Box sx={contentAreaSx}>
            <PlanApprovalContextRenderer plan={DEMO_PLAN_CONTENT} enableCollapse={false} />

            <Divider sx={contentDividerSx} />

            <UserOptionsRenderer
              options={DEMO_PLAN_OPTIONS}
              isActive={false}
              selectedOptionIndex={DEMO_SELECTED_OPTION_INDEX}
              selectionColor={PLAN_SELECTION_COLOR}
              showTitle={true}
            />
          </Box>
        </Box>
      </Fade>
    </Box>
  )
}

const sectionSubtitleSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: { xs: '1rem', md: '1.1rem' },
  fontWeight: 400,
  textAlign: 'center',
  mb: 2,
}

const chromeContainerSx: SxProps<Theme> = (theme) => ({
  borderRadius: 2,
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(62, 39, 35, 0.10)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 4px 24px rgba(0, 0, 0, 0.3)'
      : '0 4px 24px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
  backgroundColor: 'background.paper',
  width: '100%',
  maxWidth: 900,
})

const contentAreaSx: SxProps<Theme> = {
  pt: 5,
  px: 3,
  pb: 3,
}

const contentDividerSx: SxProps<Theme> = {
  my: 2,
}
